import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import AddToEHR from "../components/AddToEHR";
import {
  Shield, AlertTriangle, CheckCircle, Clock, Calendar, User,
  RefreshCw, ChevronLeft, FileText, ClipboardCheck, Activity,
  Bell, Eye, Check, X, AlertCircle, UserCheck, FileSignature,
  CalendarCheck, Stethoscope, Users, Home, Building
} from "lucide-react";
import { AddToRecordButton } from "../components/AddToRecord";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ALERT_TYPES = {
  five_day_assessment: { label: "5-Day Assessment Due", icon: Calendar, color: "bg-red-100 text-red-800" },
  visit_gap: { label: "Visit Gap", icon: Clock, color: "bg-orange-100 text-orange-800" },
  recertification: { label: "Recertification Due", icon: FileSignature, color: "bg-purple-100 text-purple-800" },
  f2f: { label: "F2F Required", icon: UserCheck, color: "bg-blue-100 text-blue-800" },
  aide_supervision: { label: "Aide Supervision Due", icon: Users, color: "bg-cyan-100 text-cyan-800" },
  unsigned_document: { label: "Unsigned Document", icon: FileText, color: "bg-yellow-100 text-yellow-800" },
  medication_review: { label: "Med Review Due", icon: ClipboardCheck, color: "bg-indigo-100 text-indigo-800" }
};

const PRIORITY_CONFIG = {
  critical: { label: "Critical", color: "bg-red-600 text-white", weight: 4 },
  high: { label: "High", color: "bg-orange-500 text-white", weight: 3 },
  medium: { label: "Medium", color: "bg-yellow-500 text-white", weight: 2 },
  low: { label: "Low", color: "bg-gray-400 text-white", weight: 1 }
};

export default function ComplianceManagement() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [activeTab, setActiveTab] = useState("alerts");
  const [alerts, setAlerts] = useState([]);
  const [filterPriority, setFilterPriority] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [resolveDialog, setResolveDialog] = useState(false);
  const [resolveNotes, setResolveNotes] = useState("");
  
  const [stats, setStats] = useState({
    total_active_alerts: 0,
    critical_alerts: 0,
    high_alerts: 0,
    medium_alerts: 0,
    alerts_resolved_today: 0,
    unsigned_documents: { total: 0 },
    by_type: {}
  });

  const [unsignedDocs, setUnsignedDocs] = useState([]);
  const [batchSignList, setBatchSignList] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("hospice_token");
      
      // Fetch dashboard summary
      const response = await fetch(`${API_URL}/api/ehr/compliance/dashboard/summary`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data.summary || {});
        setAlerts(data.recent_alerts || []);
        setUnsignedDocs(data.unsigned_documents_list || []);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load compliance data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 2 minutes
    const interval = setInterval(fetchData, 120000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const runComplianceScan = async () => {
    try {
      setScanning(true);
      const token = localStorage.getItem("hospice_token");
      
      const response = await fetch(`${API_URL}/api/ehr/compliance/scan/all`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(`Scanned ${result.charts_scanned} charts, found ${result.alerts_generated} new alerts`);
        fetchData();
      } else {
        toast.error("Compliance scan failed");
      }
    } catch (error) {
      console.error("Error running scan:", error);
      toast.error("Failed to run compliance scan");
    } finally {
      setScanning(false);
    }
  };

  const resolveAlert = async () => {
    if (!selectedAlert) return;

    try {
      const token = localStorage.getItem("hospice_token");
      const userId = localStorage.getItem("userId") || "clinician";
      
      const response = await fetch(`${API_URL}/api/ehr/compliance/alerts/${selectedAlert.id}/resolve`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          resolved_by: userId,
          resolution_notes: resolveNotes
        })
      });

      if (response.ok) {
        toast.success("Alert resolved");
        setResolveDialog(false);
        setSelectedAlert(null);
        setResolveNotes("");
        fetchData();
      } else {
        toast.error("Failed to resolve alert");
      }
    } catch (error) {
      console.error("Error resolving alert:", error);
      toast.error("Failed to resolve alert");
    }
  };

  const handleBatchSign = async () => {
    if (batchSignList.length === 0) {
      toast.error("No documents selected for signing");
      return;
    }

    try {
      const token = localStorage.getItem("hospice_token");
      const userId = localStorage.getItem("userId") || "clinician";
      
      const response = await fetch(`${API_URL}/api/ehr/compliance/documents/batch-sign`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          document_ids: batchSignList,
          signed_by: userId
        })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Signed ${result.signed_count} documents`);
        setBatchSignList([]);
        fetchData();
      } else {
        toast.error("Batch signing failed");
      }
    } catch (error) {
      console.error("Error batch signing:", error);
      toast.error("Failed to batch sign documents");
    }
  };

  const toggleBatchSelect = (docId) => {
    setBatchSignList(prev => 
      prev.includes(docId) 
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  const selectAllDocs = () => {
    if (batchSignList.length === unsignedDocs.length) {
      setBatchSignList([]);
    } else {
      setBatchSignList(unsignedDocs.map(d => d.id));
    }
  };

  const filteredAlerts = alerts.filter(alert => {
    if (filterPriority !== "all" && alert.priority !== filterPriority) return false;
    if (filterType !== "all" && alert.alert_type !== filterType) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        alert.patient_name?.toLowerCase().includes(search) ||
        alert.description?.toLowerCase().includes(search)
      );
    }
    return true;
  }).sort((a, b) => {
    const weightA = PRIORITY_CONFIG[a.priority]?.weight || 0;
    const weightB = PRIORITY_CONFIG[b.priority]?.weight || 0;
    return weightB - weightA;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-900 via-red-800 to-orange-800 text-white">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                className="text-white hover:bg-white/20"
                onClick={() => navigate("/hospice-ehr/command-center")}
                data-testid="back-btn"
              >
                <ChevronLeft className="w-5 h-5 mr-1" /> Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-3">
                  <Shield className="w-7 h-7" />
                  Compliance Command
                </h1>
                <p className="text-red-100 mt-1">Real-time Regulatory Compliance Engine</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <AddToEHR 
                buttonVariant="default"
                buttonSize="default"
                buttonText="Add to EHR"
                className="bg-white text-red-800 hover:bg-red-50"
              />
              <Button
                onClick={runComplianceScan}
                disabled={scanning}
                className="bg-white text-red-800 hover:bg-red-50"
                data-testid="scan-btn"
              >
                {scanning ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Scanning...
                  </>
                ) : (
                  <>
                    <Shield className="w-4 h-4 mr-2" /> Run Full Scan
                  </>
                )}
              </Button>
              <Button
                variant="ghost"
                className="text-white hover:bg-white/20"
                onClick={fetchData}
                data-testid="refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="grid grid-cols-5 gap-4">
            <div className="flex items-center gap-3 p-3 bg-red-900/50 rounded-lg border border-red-700/50">
              <AlertTriangle className="w-8 h-8 text-red-400" />
              <div>
                <p className="text-2xl font-bold text-red-300">{stats.critical_alerts || 0}</p>
                <p className="text-xs text-red-400">Critical</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-orange-900/50 rounded-lg border border-orange-700/50">
              <AlertCircle className="w-8 h-8 text-orange-400" />
              <div>
                <p className="text-2xl font-bold text-orange-300">{stats.high_alerts || 0}</p>
                <p className="text-xs text-orange-400">High Priority</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-yellow-900/50 rounded-lg border border-yellow-700/50">
              <Clock className="w-8 h-8 text-yellow-400" />
              <div>
                <p className="text-2xl font-bold text-yellow-300">{stats.medium_alerts || 0}</p>
                <p className="text-xs text-yellow-400">Medium</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-blue-900/50 rounded-lg border border-blue-700/50">
              <FileSignature className="w-8 h-8 text-blue-400" />
              <div>
                <p className="text-2xl font-bold text-blue-300">{stats.unsigned_documents?.total || 0}</p>
                <p className="text-xs text-blue-400">Unsigned Docs</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-green-900/50 rounded-lg border border-green-700/50">
              <CheckCircle className="w-8 h-8 text-green-400" />
              <div>
                <p className="text-2xl font-bold text-green-300">{stats.alerts_resolved_today || 0}</p>
                <p className="text-xs text-green-400">Resolved Today</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 bg-slate-800">
            <TabsTrigger value="alerts" className="gap-2 data-[state=active]:bg-red-600">
              <AlertTriangle className="w-4 h-4" /> Active Alerts
              {stats.total_active_alerts > 0 && (
                <Badge className="ml-1 bg-red-500">{stats.total_active_alerts}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="signatures" className="gap-2 data-[state=active]:bg-blue-600">
              <FileSignature className="w-4 h-4" /> Batch Signatures
              {unsignedDocs.length > 0 && (
                <Badge className="ml-1 bg-blue-500">{unsignedDocs.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="dashboard" className="gap-2 data-[state=active]:bg-purple-600">
              <Activity className="w-4 h-4" /> Analytics
            </TabsTrigger>
          </TabsList>

          {/* Alerts Tab */}
          <TabsContent value="alerts">
            <Card className="shadow-lg bg-slate-800 border-slate-700">
              <CardHeader className="border-b border-slate-700">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg text-white">Compliance Alerts</CardTitle>
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <Input
                        placeholder="Search alerts..."
                        className="pl-9 w-48 bg-slate-700 border-slate-600 text-white"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                    <Select value={filterPriority} onValueChange={setFilterPriority}>
                      <SelectTrigger className="w-32 bg-slate-700 border-slate-600 text-white">
                        <SelectValue placeholder="Priority" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Priority</SelectItem>
                        <SelectItem value="critical">Critical</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={filterType} onValueChange={setFilterType}>
                      <SelectTrigger className="w-40 bg-slate-700 border-slate-600 text-white">
                        <SelectValue placeholder="Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        {Object.entries(ALERT_TYPES).map(([key, val]) => (
                          <SelectItem key={key} value={key}>{val.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-6 h-6 animate-spin text-red-400" />
                    <span className="ml-2 text-slate-400">Loading alerts...</span>
                  </div>
                ) : filteredAlerts.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                    <p className="font-medium text-green-400">All Clear!</p>
                    <p className="text-sm mt-1">No compliance alerts at this time</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-700">
                    {filteredAlerts.map(alert => {
                      const typeConfig = ALERT_TYPES[alert.alert_type] || ALERT_TYPES.five_day_assessment;
                      const TypeIcon = typeConfig.icon;
                      const priorityConfig = PRIORITY_CONFIG[alert.priority] || PRIORITY_CONFIG.medium;
                      
                      return (
                        <div
                          key={alert.id}
                          className="p-4 hover:bg-slate-700/50 transition-colors"
                          data-testid={`alert-row-${alert.id}`}
                        >
                          <div className="flex items-start gap-4">
                            <div className={`p-2 rounded-lg ${typeConfig.color.split(' ')[0]}`}>
                              <TypeIcon className={`w-5 h-5 ${typeConfig.color.split(' ')[1]}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge className={priorityConfig.color}>
                                  {priorityConfig.label}
                                </Badge>
                                <Badge className={typeConfig.color}>
                                  {typeConfig.label}
                                </Badge>
                              </div>
                              <h4 className="font-medium text-white">{alert.patient_name || "Unknown Patient"}</h4>
                              <p className="text-sm text-slate-400 mt-1">{alert.description}</p>
                              <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  Due: {alert.due_date ? new Date(alert.due_date).toLocaleDateString() : "N/A"}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  Created: {new Date(alert.created_at).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                onClick={() => {
                                  setSelectedAlert(alert);
                                  setResolveDialog(true);
                                }}
                                className="bg-green-600 hover:bg-green-700"
                                data-testid={`resolve-alert-${alert.id}`}
                              >
                                <Check className="w-4 h-4 mr-1" /> Resolve
                              </Button>
                              <AddToRecordButton
                                chartId={alert.chart_id}
                                patientName={alert.patient_name}
                                sourceModule="compliance_alerts"
                                sourceEventId={alert.id}
                                prefillType="care_concern"
                                prefillDescription={`Compliance issue: ${typeConfig.label} - ${alert.details || ''}`}
                              />
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-slate-600 text-slate-300"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Batch Signatures Tab */}
          <TabsContent value="signatures">
            <Card className="shadow-lg bg-slate-800 border-slate-700">
              <CardHeader className="border-b border-slate-700">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg text-white flex items-center gap-2">
                      <FileSignature className="w-5 h-5" /> IDG / POC Batch Signing
                    </CardTitle>
                    <CardDescription className="text-slate-400">
                      Sign multiple documents at once for IDG and Plan of Care
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button
                      variant="outline"
                      className="border-slate-600 text-slate-300"
                      onClick={selectAllDocs}
                    >
                      {batchSignList.length === unsignedDocs.length ? "Deselect All" : "Select All"}
                    </Button>
                    <Button
                      onClick={handleBatchSign}
                      disabled={batchSignList.length === 0}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="batch-sign-btn"
                    >
                      <FileSignature className="w-4 h-4 mr-2" />
                      Sign Selected ({batchSignList.length})
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {unsignedDocs.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                    <p className="font-medium text-green-400">All Documents Signed!</p>
                    <p className="text-sm mt-1">No documents pending signature</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-700">
                    {unsignedDocs.map(doc => (
                      <div
                        key={doc.id}
                        className={`p-4 hover:bg-slate-700/50 transition-colors cursor-pointer ${
                          batchSignList.includes(doc.id) ? "bg-blue-900/30" : ""
                        }`}
                        onClick={() => toggleBatchSelect(doc.id)}
                        data-testid={`doc-row-${doc.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${
                            batchSignList.includes(doc.id) 
                              ? "bg-blue-600 border-blue-600" 
                              : "border-slate-500"
                          }`}>
                            {batchSignList.includes(doc.id) && (
                              <Check className="w-4 h-4 text-white" />
                            )}
                          </div>
                          <div className="p-2 bg-slate-700 rounded-lg">
                            <FileText className="w-5 h-5 text-slate-400" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-white">{doc.document_type || doc.type}</h4>
                            <p className="text-sm text-slate-400">
                              Patient: {doc.patient_name} • Created: {new Date(doc.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <Badge className="bg-yellow-600">Pending Signature</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="dashboard">
            <div className="grid grid-cols-2 gap-6">
              <Card className="shadow-lg bg-slate-800 border-slate-700">
                <CardHeader className="border-b border-slate-700">
                  <CardTitle className="text-lg text-white">Alert Distribution by Type</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    {Object.entries(stats.by_type || {}).map(([type, count]) => {
                      const typeConfig = ALERT_TYPES[type] || ALERT_TYPES.five_day_assessment;
                      const TypeIcon = typeConfig.icon;
                      const total = stats.total_active_alerts || 1;
                      const percentage = Math.round((count / total) * 100);
                      
                      return (
                        <div key={type} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <TypeIcon className="w-4 h-4 text-slate-400" />
                              <span className="text-sm text-slate-300">{typeConfig.label}</span>
                            </div>
                            <span className="text-sm font-medium text-white">{count}</span>
                          </div>
                          <Progress value={percentage} className="h-2" />
                        </div>
                      );
                    })}
                    {Object.keys(stats.by_type || {}).length === 0 && (
                      <p className="text-center text-slate-500 py-4">No alert data available</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-lg bg-slate-800 border-slate-700">
                <CardHeader className="border-b border-slate-700">
                  <CardTitle className="text-lg text-white">Compliance Overview</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-slate-700 rounded-lg">
                      <span className="text-slate-300">5-Day Assessments</span>
                      <Badge className={stats.by_type?.five_day_assessment > 0 ? "bg-red-600" : "bg-green-600"}>
                        {stats.by_type?.five_day_assessment || 0} Due
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-700 rounded-lg">
                      <span className="text-slate-300">Recertifications</span>
                      <Badge className={stats.by_type?.recertification > 0 ? "bg-purple-600" : "bg-green-600"}>
                        {stats.by_type?.recertification || 0} Due
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-700 rounded-lg">
                      <span className="text-slate-300">F2F Encounters</span>
                      <Badge className={stats.by_type?.f2f > 0 ? "bg-blue-600" : "bg-green-600"}>
                        {stats.by_type?.f2f || 0} Required
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-700 rounded-lg">
                      <span className="text-slate-300">Aide Supervision</span>
                      <Badge className={stats.by_type?.aide_supervision > 0 ? "bg-cyan-600" : "bg-green-600"}>
                        {stats.by_type?.aide_supervision || 0} Due
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-700 rounded-lg">
                      <span className="text-slate-300">Visit Gaps (7+ days)</span>
                      <Badge className={stats.by_type?.visit_gap > 0 ? "bg-orange-600" : "bg-green-600"}>
                        {stats.by_type?.visit_gap || 0} Patients
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Resolve Alert Dialog */}
      <Dialog open={resolveDialog} onOpenChange={setResolveDialog}>
        <DialogContent className="max-w-lg bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Resolve Alert</DialogTitle>
            <DialogDescription className="text-slate-400">
              Mark this compliance alert as resolved
            </DialogDescription>
          </DialogHeader>
          {selectedAlert && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-slate-700 rounded-lg">
                <h4 className="font-medium text-white mb-2">{selectedAlert.patient_name}</h4>
                <p className="text-sm text-slate-400">{selectedAlert.description}</p>
                <Badge className={ALERT_TYPES[selectedAlert.alert_type]?.color || "bg-gray-500"}>
                  {ALERT_TYPES[selectedAlert.alert_type]?.label || selectedAlert.alert_type}
                </Badge>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Resolution Notes</Label>
                <Textarea
                  value={resolveNotes}
                  onChange={(e) => setResolveNotes(e.target.value)}
                  placeholder="Document how this was resolved..."
                  rows={3}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveDialog(false)} className="border-slate-600 text-slate-300">
              Cancel
            </Button>
            <Button onClick={resolveAlert} className="bg-green-600 hover:bg-green-700" data-testid="confirm-resolve-btn">
              <Check className="w-4 h-4 mr-2" /> Mark Resolved
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
